import { gql } from "@apollo/client";
import { useApolloClient, useQuery } from "@apollo/client/react";
import _ from "lodash";
import { useMemo, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import {
  BuildingIcon,
  CalendarDaysIcon,
  CoinsIcon,
  CreditCardIcon,
  DollarSignIcon,
  FileUpIcon,
  HashIcon,
  MergeIcon,
  RepeatIcon,
  SplitIcon,
  TableIcon,
  TextAlignStartIcon,
} from "lucide-react";

import {
  FinancialAccountFragment,
  FinancialAccountSyncStateEnum,
  FinancialAccountTypeEnum,
} from "@/__generated__/graphql";
import {
  FieldPaginatedTableValueOptions,
  FilterType,
  SortableType,
  TableColumnType,
} from "@/components/@types";
import { Icon } from "@/components/atoms/Icon";
import { useOpenRecord } from "@/app/app/_/routing";
import { SelectableCardList } from "@/components/molecules/SelectableCardList";
import {
  SELECTABLE_LIST_ITEM_ROW_WITH_OVERLINE_HEIGHT,
  SelectableListItemRow,
  SelectableListItemRowOverline,
  SelectableListItemRowTitleEnd,
  SelectableListItemRowTitleEndEmphasis,
} from "@/components/molecules/SelectableListItemRow";
import { SimpleEnum } from "@/components/molecules/SimpleEnum";
import { FieldPaginatedTable } from "@/components/organisms/Field";
import {
  useNodeField,
  useNodeLayoutStore,
} from "@/components/organisms/Node/Node.hooks";
import { UtilityBar } from "@/components/organisms/UtilityBar";
import {
  columnsToFilterDefinitions,
  columnsToSortableDefinitions,
} from "@/components/organisms/UtilityBar/UtilityBar.utils";
import { Mutations, Queries } from "@/constants";
import { BoxForEntity } from "@/components/atoms/BoxForEntity";
import { EnumColors, Values } from "@/constants/values";
import { iconsByTypename } from "@/objects/@icons";
import { ObjectCardProps, ObjectType } from "@/objects/@types";
import { toColumns } from "@/objects/@utils";
import { getReferenceValueOptionsByTypename, Objects } from "@/objects";
import { State } from "@/state";
import { Utils } from "@/utils";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";

const FINANCIAL_ACCOUNT_TRANSACTION_COLUMN_KEYS = [
  "self",
  "effectiveDate",
  "amount",
  "categoryObj",
  "tags",
] as const;

/** The transactions group's chips before its toolbar has set any. */
const NO_TRANSACTION_CHIPS: {
  filters: FilterType[];
  sortables: SortableType[];
} = { filters: [], sortables: [] };

function FinancialAccountTransactionsTab({ size }: { size: "sm" | "md" }) {
  const store = useNodeLayoutStore();
  const fieldValues = store(useShallow((state) => state.fieldValues));
  const { fieldDefinition } = useNodeField(store, "transactions");
  const { filters, sortables } = store(
    (state) => state.groupViews.transactions ?? NO_TRANSACTION_CHIPS,
  );

  if (!fieldDefinition || fieldDefinition.type !== "paginatedTable") {
    return null;
  }

  const valueOptions = (fieldDefinition.getValueOptions?.(fieldValues) ?? {
    columns: [],
    connection: {
      query: Queries.TRANSACTIONS_CONNECTION_QUERY,
      queryKey: "transactionsConnection",
      pageSize: 50,
      getVariables: () => ({ entityIdentifiers: [] }),
    },
  }) as FieldPaginatedTableValueOptions;

  return (
    <div
      // A list, on the recessed surface lists sit on, under its toolbar.
      className="flex-1 min-h-0 w-full max-w-full flex flex-col overflow-hidden bg-muted dark:bg-[#151719]"
    >
      <FieldPaginatedTable
        data={fieldValues}
        valueOptions={valueOptions}
        filters={filters}
        sortables={sortables}
        cellSize={size}
        style={{ width: "100%", height: "100%", flex: 1, minHeight: 0 }}
      />
    </div>
  );
}

/**
 * The transactions group's toolbar: search, sort and filter chips over the
 * table's own columns, as the transactions list has over its. What it sets is
 * kept in the node store for the table below to be asked with.
 */
function FinancialAccountTransactionsToolbar({
  isInPopper = false,
}: {
  isInPopper?: boolean;
}) {
  const store = useNodeLayoutStore();
  const fieldValues = store(useShallow((state) => state.fieldValues));
  const { fieldDefinition } = useNodeField(store, "transactions");
  const { filters, sortables } = store(
    (state) => state.groupViews.transactions ?? NO_TRANSACTION_CHIPS,
  );
  const columns = useMemo(
    () =>
      fieldDefinition?.type === "paginatedTable"
        ? ((fieldDefinition.getValueOptions?.(fieldValues)?.columns ??
            []) as TableColumnType[])
        : [],
    [fieldDefinition, fieldValues],
  );
  const filterDefinitions = useMemo(
    () => columnsToFilterDefinitions(columns),
    [columns],
  );
  const sortableDefinitions = useMemo(
    () => columnsToSortableDefinitions(columns),
    [columns],
  );
  const setChips = (
    chips: Partial<{ filters: FilterType[]; sortables: SortableType[] }>,
  ) =>
    store.setState((state) => ({
      groupViews: {
        ...state.groupViews,
        transactions: {
          ...(state.groupViews.transactions ?? NO_TRANSACTION_CHIPS),
          ...chips,
        },
      },
    }));

  return (
    <UtilityBar
      disablePadding
      isSearchable
      filtersInPopper={isInPopper}
      filters={filters}
      filterDefinitions={filterDefinitions}
      onFiltersChange={(next) => setChips({ filters: next })}
      sortables={sortables}
      sortableDefinitions={sortableDefinitions}
      onSortablesChange={(next) => setChips({ sortables: next })}
    />
  );
}

type StatementRow = {
  id: string;
  identifier: string;
  name?: string | null;
  description?: string | null;
  dateIssued?: string | null;
  createdAt?: string | null;
  /** What the statement says of itself, where it was read as one. */
  statement?: {
    periodStart?: string | null;
    periodEnd?: string | null;
    openingBalance?: string | null;
    closingBalance?: string | null;
    total?: string | null;
  } | null;
};

/** What the list's rows are while it loads: a typical statement. */
const STATEMENT_PLACEHOLDER: StatementRow = {
  id: "placeholder",
  identifier: "placeholder",
  name: "Business Chequing statement",
  description: "Royal Bank of Canada 4421, 2026-01-01 to 2026-01-31",
  dateIssued: "2026-01-31T00:00:00Z",
  createdAt: "2026-02-02T00:00:00Z",
  statement: {
    periodStart: "2026-01-01",
    periodEnd: "2026-01-31",
    openingBalance: "12400.00",
    closingBalance: "15230.55",
    total: "2830.55",
  },
};

/**
 * The account's statements - the documents linked back to it - latest first.
 * Loaded here rather than with the account, as a group's rows are; a
 * statement opens as its own page.
 */
function FinancialAccountStatementsTab() {
  const store = useNodeLayoutStore();
  const accountId = store(
    (state) => (state.fieldValues as { id?: string })?.id,
  );
  const openRecord = useOpenRecord();
  const { data, loading } = useQuery(
    Queries.FINANCIAL_ACCOUNT_STATEMENTS_QUERY,
    {
      variables: { id: accountId! },
      skip: !accountId,
      fetchPolicy: "cache-and-network",
    },
  );
  const statements = useMemo<StatementRow[]>(() => {
    const node = data?.node as
      | { statements?: StatementRow[] }
      | null
      | undefined;
    return node?.statements ?? [];
  }, [data]);

  return (
    <div
      // A list, on the recessed surface lists sit on.
      className="flex-1 min-h-0 w-full flex flex-col overflow-hidden bg-muted dark:bg-[#151719]"
      data-testid="financial-account-statements"
    >
      <SelectableCardList
        // The triage lists' size and variant: their insets, corners and type.
        size="sm"
        variant="outlined"
        layout="cards"
        // A row with a line over its title and one under it: the list gives
        // each that room, so rows and their skeletons are one height.
        estimatedItemHeight={SELECTABLE_LIST_ITEM_ROW_WITH_OVERLINE_HEIGHT}
        items={statements}
        loading={loading && statements.length === 0}
        placeholderItem={STATEMENT_PLACEHOLDER}
        getItemKey={(statement) => statement.id}
        onItemClick={(statement) =>
          openRecord({ id: statement.id, identifier: statement.identifier })
        }
        emptyState={{
          title: "No statements yet",
          description:
            "A bank statement dropped in a thread or uploaded for this account shows here.",
        }}
        renderItem={({ item: statement, isLoading }) => {
          const summary = statement.statement;
          const issued = statement.dateIssued ?? statement.createdAt;
          // The period it covers where it says, else the day it was issued.
          const dates =
            summary?.periodStart && summary?.periodEnd
              ? `${Utils.fDate(new Date(summary.periodStart))} – ${Utils.fDate(new Date(summary.periodEnd))}`
              : issued
                ? Utils.fDate(new Date(issued))
                : null;
          const money = (value?: string | null) =>
            value == null ? null : Utils.fCurrency(Number(value), false, true);
          const balances = [
            money(summary?.openingBalance) &&
              `Starting ${money(summary?.openingBalance)}`,
            money(summary?.closingBalance) &&
              `Ending ${money(summary?.closingBalance)}`,
          ].filter(Boolean);
          // Set as a triage row is: what it covers small above - its
          // balances, the dates at the end - its name with what it comes to
          // below, and what it says it is under that.
          return (
            <SelectableListItemRow
              overline={
                balances.length > 0 ? (
                  <SelectableListItemRowOverline>
                    {balances.join(" · ")}
                  </SelectableListItemRowOverline>
                ) : null
              }
              overlineEnd={
                dates ? (
                  <SelectableListItemRowTitleEnd>
                    {dates}
                  </SelectableListItemRowTitleEnd>
                ) : null
              }
              title={statement.name || "Statement"}
              description={statement.description || null}
              titleEnd={
                summary?.total != null ? (
                  <SelectableListItemRowTitleEndEmphasis>
                    {money(summary.total)}
                  </SelectableListItemRowTitleEndEmphasis>
                ) : null
              }
              isLoading={isLoading}
              rootDataAttrs={{
                "data-testid": "financial-account-statement",
                "data-statement-id": statement.identifier,
              }}
            />
          );
        }}
      />
    </div>
  );
}

function FinancialAccountCard({
  data,
  actions,
  isLoading = false,
}: ObjectCardProps<FinancialAccountFragment>) {
  // Actions arrive resolved from ``ObjectType.getActions(data)``; the
  // card fires them by either invoking ``onClick`` (popper-style) or by
  // running ``mutationOptions`` directly so the same shape works in both
  // a tab popover and an inline icon-button row. Plaid sync triggers a
  // backend Pub/Sub job, so we just optimistically show a spinner — the
  // resulting transactions will arrive via the standard ``OBJECTS_CHANGED``
  // subscription rather than the mutation response.
  const client = useApolloClient();
  const [pendingActionLabel, setPendingActionLabel] = useState<string | null>(
    null,
  );
  const onRunAction = async (action: (typeof actions)[number]) => {
    if (pendingActionLabel) return;
    setPendingActionLabel(action.label);
    try {
      if (action.mutationOptions) {
        await client.mutate({
          mutation: action.mutationOptions.mutation,
          ...(action.mutationOptions.options as Record<string, unknown>),
        });
      } else {
        action.onClick?.();
      }
    } finally {
      setPendingActionLabel(null);
    }
  };
  // Last 4 digits — prefer ``mask`` (already trimmed by Plaid) and fall back
  // to slicing ``accountNumber`` so manually-entered accounts still render
  // sensibly. Full account numbers should never be shown in card view.
  const accountSuffix =
    data?.mask || (data?.accountNumber ? data.accountNumber.slice(-4) : null);
  const institutionName =
    data?.financialInstitution?.institutionName || "Unknown institution";
  const institutionLine = accountSuffix
    ? `${institutionName} · ${accountSuffix}`
    : institutionName;
  const subtype = (data?.subtype || data?.accountType || data?.type || "")
    .toString()
    .toLowerCase();
  // A card says two figures, because they are two different things. What the
  // bank says is in the account is every line that has landed in it, coded or
  // not. What is booked is the balance of the account's place in the chart,
  // which only moves when a line is coded into a journal entry - so a
  // statement just imported reads as the full amount, none of it booked.
  // Both are aliased on the fragment (a charted account answers ``balance``
  // with a metric object) and Decimal scalars arrive as strings.
  const balanceRaw = data?.financialAccountBalance;
  const balance =
    balanceRaw == null || balanceRaw === ""
      ? null
      : Utils.fCurrency(Number(balanceRaw), false, true);
  const bookedRaw =
    data?.financialAccountChartedAccount?.balance?.endingValue ?? null;
  // Said only when the two differ: an account with nothing left to code has
  // one figure, and a second line repeating it is noise.
  const booked =
    bookedRaw == null ||
    balanceRaw == null ||
    Number(bookedRaw) === Number(balanceRaw)
      ? null
      : Utils.fCurrency(Number(bookedRaw), false, true);
  // ``syncState`` is the authoritative server-side flag flipped by the
  // ``sync_financial_account`` mutation and reset by the Pub/Sub
  // subscriber when the Plaid pull finishes. We OR it with the local
  // ``pendingActionLabel`` so the spinner shows immediately on click,
  // before the mutation response (and the resulting ``syncState`` cache
  // update) lands.
  const isSyncingFromServer =
    data?.syncState === FinancialAccountSyncStateEnum.Syncing;
  const isSyncFailed = data?.syncState === FinancialAccountSyncStateEnum.Failed;
  const isPlaidLinked = Boolean(data?.plaidBankAccountId);
  // Plaid told us (via webhook or via a sync attempt) that this Item
  // is in ``ITEM_LOGIN_REQUIRED`` / ``PENDING_EXPIRATION`` /
  // ``PENDING_DISCONNECT``. The chip + action below both reflect this
  // — "Reconnection needed" is rendered as a warning (user-actionable
  // lifecycle event) rather than danger (system fault).
  //
  // We also treat ``FAILED`` on a Plaid-linked account as
  // reconnect-eligible. The overwhelming majority of sustained Plaid
  // failures are credentials-related, and the only useful action a
  // user can take on a stuck ``FAILED`` row is to retry / reconnect.
  // Surfacing Reconnect here resolves the common case without waiting
  // for a webhook redelivery; if the Item is actually healthy, Plaid
  // Link's update mode will detect that and exit cleanly. Defensive
  // fallback for any state the server-side detection misses.
  const needsReconnect =
    data?.syncState === FinancialAccountSyncStateEnum.ReconnectRequired ||
    (isPlaidLinked && isSyncFailed);
  const lastSyncedAt = data?.syncCompletedAt
    ? new Date(data.syncCompletedAt)
    : null;
  const lastSynced =
    lastSyncedAt != null ? Utils.fLastSyncedAt(lastSyncedAt) : null;
  return (
    <div className="flex flex-col gap-1.5 h-full min-h-0">
      <Typography
        level="body-xs"
        className="truncate uppercase [letter-spacing:0.05em] text-muted-foreground/80"
      >
        <Skeleton loading={isLoading} variant="inline">
          {institutionLine}
        </Skeleton>
      </Typography>

      {balance != null && (
        <div className="flex flex-col gap-0.5">
          <Typography level="mono-h2" className="[line-height:1.1]">
            <Skeleton loading={isLoading} variant="inline">
              {balance}
            </Skeleton>
          </Typography>
          {booked != null && (
            <Typography
              level="body-xs"
              data-testid="account-booked-balance"
              className="text-muted-foreground/80"
            >
              <Skeleton loading={isLoading} variant="inline">
                {`${booked} booked`}
              </Skeleton>
            </Typography>
          )}
        </div>
      )}

      {isPlaidLinked && lastSynced && (
        <div className="flex flex-col gap-px">
          <Typography level="body-xs" className="text-muted-foreground/80">
            <Skeleton loading={isLoading} variant="inline">
              Last synced
            </Skeleton>
          </Typography>
          {lastSynced.isRelative ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Typography
                  level="body-sm"
                  className="text-muted-foreground w-fit"
                >
                  <Skeleton loading={isLoading} variant="inline">
                    {lastSynced.label}
                  </Skeleton>
                </Typography>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {Utils.fDateTime(lastSyncedAt!)}
              </TooltipContent>
            </Tooltip>
          ) : (
            <Typography level="body-sm" className="text-muted-foreground">
              <Skeleton loading={isLoading} variant="inline">
                {lastSynced.label}
              </Skeleton>
            </Typography>
          )}
        </div>
      )}

      <div className="mt-auto flex items-center gap-1 flex-wrap">
        {subtype && (
          <Skeleton
            loading={isLoading}
            variant="rectangular"
            className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
          >
            <SimpleEnum
              label={subtype}
              enumColor={EnumColors.GREY_1}
              size="sm"
            />
          </Skeleton>
        )}
        {(data?.integrationProvider || data?.plaidBankAccountId) && (
          <Skeleton
            loading={isLoading}
            variant="rectangular"
            className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
          >
            <SimpleEnum
              label={(data?.integrationProvider ?? "plaid").toLowerCase()}
              enumColor={EnumColors.BLUE}
              size="sm"
            />
          </Skeleton>
        )}
        {isSyncingFromServer && (
          <SimpleEnum label="Syncing…" enumColor={EnumColors.BLUE} size="sm" />
        )}
        {needsReconnect && (
          <SimpleEnum
            label="Reconnection needed"
            enumColor={EnumColors.ORANGE}
            size="sm"
          />
        )}
        {isSyncFailed && !needsReconnect && (
          <SimpleEnum
            label="Sync failed"
            enumColor={EnumColors.RED}
            size="sm"
          />
        )}
        {actions.length > 0 && (
          <Skeleton
            loading={isLoading}
            variant="rectangular"
            className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit] ml-auto"
          >
            <div className="ml-auto flex gap-0.5">
              {actions.map((action) => {
                const isSyncAction = action.label === "Sync";
                const isPending =
                  pendingActionLabel === action.label ||
                  (isSyncAction && isSyncingFromServer);
                return (
                  <Tooltip key={action.label}>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        disabled={
                          isPending ||
                          (pendingActionLabel !== null && !isPending)
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isPending) return;
                          void onRunAction(action);
                        }}
                      >
                        {isPending ? (
                          <Spinner />
                        ) : (
                          <Icon ReactIcon={action.icon} size="md" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      {isSyncAction && isSyncingFromServer
                        ? "Syncing…"
                        : action.label}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </Skeleton>
        )}
      </div>
    </div>
  );
}

const object: ObjectType<FinancialAccountFragment> = {
  humanName: "bank account",
  typename: "FinancialAccountObject",
  report: {
    source: "financial_accounts",
    description: "The bank and card accounts money moves through",
    asks: "How is the cash split across the accounts?",
  },
  ReactIcon: CreditCardIcon,
  isEntity: false,
  titleFieldKey: "name",
  Card: FinancialAccountCard,
  placeholder: {
    __typename: "FinancialAccountObject",
    id: btoa("FinancialAccountObject:placeholder"),
    identifier: "placeholder",
    name: "Business Chequing",
    mask: "2210",
    type: FinancialAccountTypeEnum.Depository,
    subtype: "checking",
    plaidBankAccountId: "placeholder",
    syncState: FinancialAccountSyncStateEnum.Idle,
    syncCompletedAt: "2026-01-15T10:00:00Z",
    financialAccountBalance: "12480.35",
    financialAccountChartedAccount: null,
    financialInstitution: {
      id: btoa("FinancialInstitutionObject:placeholder"),
      identifier: "placeholder",
      institutionName: "Northwind Bank",
      organization: null,
      entity: {
        id: btoa("EntityObject:placeholder"),
        identifier: "placeholder",
        name: "Elm Street Coffee",
      },
    },
  } as FinancialAccountFragment,
  getTitle: (data) => {
    return data?.name;
  },
  getEntityId: (data) => {
    return data?.financialInstitution?.entity?.id;
  },
  getOrganization: (data) => data?.financialInstitution?.organization,
  getActions: (data) => {
    const actions = [];
    // Plaid sync is the only external-provider sync currently wired up
    // through ``PlaidSyncRequestedTopic``. Other providers (manual, etc.)
    // have nothing to refresh, so we hide the action rather than showing
    // a button that errors on click. ``integrationProvider`` resolves
    // from the model's ``external_provider`` column, which the Plaid
    // sync flow leaves null on the account row (it only sets it on
    // transactions); use ``plaidBankAccountId`` as the authoritative
    // "linked through Plaid" signal instead.
    if (data?.plaidBankAccountId) {
      // When the institution is awaiting user action (Plaid returned
      // ``ITEM_LOGIN_REQUIRED`` etc., either via webhook or sync),
      // swap the Sync action for a Reconnect command. ``Sync`` would
      // just immediately fail back into ``RECONNECT_REQUIRED`` again,
      // and the cleared, accumulated state we want is "user clicked
      // the chip-adjacent button → Plaid Link update mode".
      //
      // ``FAILED`` is ALSO treated as reconnect-eligible — see the
      // comment on ``needsReconnect`` in the card above. Defensive
      // fallback for legacy rows + edge cases where the server-side
      // RECONNECT_REQUIRED detection misses.
      const isReconnectNeeded =
        data?.syncState === FinancialAccountSyncStateEnum.ReconnectRequired ||
        data?.syncState === FinancialAccountSyncStateEnum.Failed;
      if (isReconnectNeeded) {
        actions.push({
          label: "Reconnect",
          icon: RepeatIcon,
          isIconButton: true,
          onClick: () => {
            const institutionIdentifier =
              data?.financialInstitution?.identifier;
            if (!institutionIdentifier) return;
            State.getState().pushToCommandStack({
              commandDefinitionId: "reconnect-bank-account",
              context: {
                financialInstitutionIdentifier: institutionIdentifier,
                institutionName:
                  data?.financialInstitution?.institutionName ?? null,
              },
            });
          },
        });
      } else {
        actions.push({
          label: "Sync",
          icon: RepeatIcon,
          isIconButton: true,
          mutationOptions: {
            mutation: Mutations.SYNC_FINANCIAL_ACCOUNT,
            options: {
              variables: {
                input: {
                  financialAccountIdentifier: data?.identifier,
                },
              },
            },
          },
        });
      }
    }
    // CSV import supplements Plaid sync for historical gaps or one-off
    // uploads. Row dedup on import keeps overlap with the next sync from
    // creating duplicates. ``onClick`` opens the upload command rather
    // than firing a mutation directly because we need a dropzone +
    // summary panel, which the action popper doesn't render.
    // The same account can reach the books twice - the name a statement
    // prints against the name the bank sends - and then its money is in two
    // places, neither of them the account's own. This is how the two are made
    // one again: the duplicate is picked here, the account kept is picked next.
    if (data?.identifier) {
      actions.push({
        label: "Merge into…",
        icon: MergeIcon,
        isIconButton: true,
        onClick: () => {
          State.getState().pushToCommandStack({
            commandDefinitionId: "merge-bank-accounts",
            context: {
              sourceIdentifier: data.identifier,
              sourceName: data?.name ?? null,
              // An account is merged within one entity's books, never across
              // two: what the list offers is that entity's accounts alone.
              entityIdentifier:
                data?.financialInstitution?.entity?.identifier ?? null,
            },
          });
        },
      });
    }
    if (data?.identifier) {
      actions.push({
        label: "Upload transactions",
        icon: FileUpIcon,
        isIconButton: true,
        onClick: () => {
          State.getState().pushToCommandStack({
            commandDefinitionId: "upload-to-financial-account",
            context: {
              financialAccountIdentifier: data.identifier,
              accountName: data?.name ?? null,
            },
          });
        },
      });
    }
    return actions;
  },
  groups: [
    {
      id: "overview",
      label: "Overview",
      isVisible: true,
      card: {
        minWidth: 280,
      },
    },
    {
      id: "statements",
      label: "Statements",
      isVisible: true,
      component: FinancialAccountStatementsTab,
      flush: true,
      // Statements and transactions share one column, tabbed: what the bank
      // said the account did, and what landed in it.
      card: {
        stack: "activity",
        stackMode: "tabs",
        flex: 1,
        isFlush: true,
      },
    },
    {
      id: "transactions",
      label: "Transactions",
      isVisible: true,
      component: FinancialAccountTransactionsTab,
      toolbar: FinancialAccountTransactionsToolbar,
      flush: true,
      card: {
        stack: "activity",
        stackMode: "tabs",
        flex: 1,
        isFlush: true,
      },
    },
  ],
  getReferenceValueOptions: (entityIdentifiers) => ({
    query: gql`
      query FinancialAccountList($entityIdentifiers: [String!]) {
        financialAccounts(entityIdentifiers: $entityIdentifiers) {
          data {
            ... on FinancialAccountObject {
              ...FinancialAccount
            }
            ... on StripeFinancialAccountObject {
              ...StripeFinancialAccount
            }
          }
        }
      }
    `,
    queryOptions: {
      variables: {
        entityIdentifiers: entityIdentifiers,
      },
    },
    onRenderOption(financialAccount: FinancialAccountFragment) {
      return financialAccount.name;
    },
  }),
  fieldDefinitions: [
    {
      objectKey: "entity",
      inputKey: "entity",
      label: "",
      type: "custom",
      ReactIcon: BuildingIcon,
      asString: (data) => data?.financialInstitution?.entity?.identifier,
      getValue: (data) => () => {
        return (
          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <BoxForEntity
              identifier={data?.financialInstitution?.entity?.identifier}
              representation="character"
            />
          </div>
        );
      },
      fieldProps: {
        exportPath: "financialInstitution.entity",
        headerFieldType: "custom",
        isSortable: false,
        isFilterable: true,
        isLockedLeft: true,
        isResizable: false,
        minWidth: Values.DEFAULT_ROW_HEIGHT,
        width: Values.DEFAULT_ROW_HEIGHT,
        maxWidth: Values.DEFAULT_ROW_HEIGHT,
      },
    },
    {
      objectKey: "self",
      inputKey: "self",
      label: "Financial Account",
      type: "reference",
      ReactIcon: iconsByTypename.FinancialAccountObject,
      asString: (data) => data?.identifier,
      getValue: (data) => {
        return {
          __typename: "FinancialAccountObject",
          logoUrl: data?.financialInstitution?.organization?.logoUrl,
          name: data?.name,
          id: data?.id,
          identifier: data?.identifier,
          entityId: data?.financialInstitution?.entity?.id,
        };
      },
      getValueOptions: (data) => ({
        isNullable: false,
        ...getReferenceValueOptionsByTypename("FinancialAccountObject")([
          data?.financialInstitution?.entity?.identifier,
        ]),
        style: "self",
      }),
      fieldProps: {
        headerFieldType: "custom",
        isSortable: false,
        isFilterable: true,
        isLockedLeft: true,
        isResizable: true,
      },
    },
    {
      objectKey: "name",
      label: "Name",
      type: "text",
      ReactIcon: TextAlignStartIcon,
      asString: (data) => data?.name,
      getValue: (data) => {
        return data?.name;
      },
      createProps: {
        isRequired: true,
      },
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "string",
        isSortable: true,
        isFilterable: true,
      },
    },
    // What it is - money held, or a card - as the list's debit and credit tabs
    // ask for it. Plaid's, or the one the account was made as.
    {
      objectKey: "type",
      label: "Type",
      type: "enum",
      ReactIcon: CreditCardIcon,
      asString: (data) => data?.type,
      getValue: (data) => data?.type,
      getValueOptions: () => ({
        enum: _.map(FinancialAccountTypeEnum, (value, key) => ({
          key,
          value,
        })),
        getColors: (mode) => ({
          [FinancialAccountTypeEnum.Depository]: EnumColors.GREEN[mode],
          [FinancialAccountTypeEnum.Credit]: EnumColors.ORANGE[mode],
        }),
      }),
      updateProps: {
        isRequired: false,
        isEditable: false,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "enum",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "institutionName",
      inputKey: "institutionName",
      label: "Institution name",
      type: "text",
      ReactIcon: DollarSignIcon,
      asString: (data) => data?.financialInstitution?.institutionName,
      getValue: (data) => data?.financialInstitution?.institutionName,
      createProps: {
        isRequired: true,
      },
      summaryProps: {
        isVisible: false,
      },
      fieldProps: {
        exportPath: "financialInstitution.institutionName",
        headerFieldType: "text",
        isSortable: false,
        isFilterable: false,
      },
    },
    {
      objectKey: "accountNumber",
      label: "Account number",
      type: "text",
      ReactIcon: HashIcon,
      asString: (data) => data?.accountNumber,
      getValue: (data) => data?.accountNumber,
      getValueForApi: (data) => {
        const row = data as FinancialAccountFragment | undefined;
        return row?.accountNumber != null ? String(row.accountNumber) : null;
      },
      createProps: {
        isRequired: true,
      },
      updateProps: {
        isRequired: true,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "string",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "routingNumber",
      label: "Routing number",
      type: "text",
      ReactIcon: SplitIcon,
      asString: (data) => data?.routingNumber,
      getValue: (data) => data?.routingNumber,
      getValueForApi: (data) => {
        const row = data as FinancialAccountFragment | undefined;
        return row?.routingNumber != null ? String(row.routingNumber) : null;
      },
      createProps: {
        isRequired: false,
      },
      updateProps: {
        isRequired: true,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "string",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "transitNumber",
      label: "Transit number",
      type: "text",
      ReactIcon: SplitIcon,
      asString: (data) => data?.transitNumber,
      getValue: (data) => data?.transitNumber,
      getValueForApi: (data) => {
        const row = data as FinancialAccountFragment | undefined;
        return row?.transitNumber != null ? String(row.transitNumber) : null;
      },
      createProps: {
        isRequired: false,
      },
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "string",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "institutionNumber",
      label: "Institution number",
      type: "text",
      ReactIcon: CoinsIcon,
      asString: (data) => data?.institutionNumber,
      getValue: (data) => data?.institutionNumber,
      getValueForApi: (data) => {
        const row = data as FinancialAccountFragment | undefined;
        return row?.institutionNumber != null
          ? String(row.institutionNumber)
          : null;
      },
      createProps: {
        isRequired: false,
      },
      updateProps: {
        isRequired: true,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "string",
        isSortable: true,
        isFilterable: true,
      },
    },
    // What the account held at the start of a day - a card's, what was owed on
    // it. Its opening transaction keeps that true in the books as more of its
    // history is brought in; changed here, it is written again.
    {
      objectKey: "openingBalance",
      label: "Starting balance",
      type: "currency",
      ReactIcon: DollarSignIcon,
      asString: (data) => data?.openingBalance ?? "",
      getValue: (data) => data?.openingBalance,
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "currency",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "openingBalanceDate",
      label: "Starting balance as of",
      type: "date",
      ReactIcon: CalendarDaysIcon,
      asString: (data) => data?.openingBalanceDate ?? "",
      getValue: (data) => data?.openingBalanceDate,
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "date",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "financialInstitution",
      label: "Institution",
      type: "text",
      ReactIcon: DollarSignIcon,
      asString: (data) => data?.financialInstitution?.institutionName,
      getValue: (data) => {
        return data?.financialInstitution?.institutionName;
      },
      summaryProps: {
        isVisible: false,
      },
      fieldProps: {
        headerFieldType: "text",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "transactions",
      label: "Transactions",
      type: "paginatedTable",
      ReactIcon: TableIcon,
      groupId: "transactions",
      linkedValuesKeys: ["identifier", "financialInstitution"],
      asString: () => "",
      getValue: () => [],
      getValueOptions: (data) => ({
        columns: toColumns(Objects.TransactionObject).filter((column) =>
          FINANCIAL_ACCOUNT_TRANSACTION_COLUMN_KEYS.includes(
            column.objectKey as (typeof FINANCIAL_ACCOUNT_TRANSACTION_COLUMN_KEYS)[number],
          ),
        ) as TableColumnType[],
        connection: {
          query: Queries.TRANSACTIONS_CONNECTION_QUERY,
          queryKey: "transactionsConnection",
          pageSize: 50,
          getVariables: (
            row: FinancialAccountFragment,
            { entityIdentifiers },
          ) => ({
            entityIdentifiers: row?.financialInstitution?.entity?.identifier
              ? [row.financialInstitution.entity.identifier]
              : entityIdentifiers,
            filter: {
              object: {
                financialAccount: {
                  field: { identifier: { exact: row?.identifier } },
                },
              },
            },
          }),
        },
      }),
      updateProps: {
        isRequired: false,
        isEditable: false,
      },
      summaryProps: {
        isVisible: false,
      },
    },
  ],
};

export { object as FinancialAccountObject };
